import axios from 'axios';

class ApiService {
  constructor() {
    this.baseURL = process.env.REACT_APP_BACKEND_URL;

    // Create axios instance
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
    });

    // Add request interceptor to include auth headers
    this.api.interceptors.request.use(
      async (config) => {
        const urlParams = new URLSearchParams(window.location.search);
        const apiKey = urlParams.get('api_key');
        const urlOrgId = urlParams.get('org_id');

        if (apiKey) {
          config.headers['X-API-KEY'] = apiKey;
          if (urlOrgId) {
            config.headers['X-Organization-ID'] = urlOrgId;
          }
        } else if (window.Clerk && window.Clerk.session) {
          const token = await window.Clerk.session.getToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        // Add organization context if available and not already set by API key
        if (!config.headers['X-Organization-ID'] && window.Clerk && window.Clerk.organization) {
          config.headers['X-Organization-ID'] = window.Clerk.organization.id;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('API Error:', error.response?.data || error.message);
        return Promise.reject(error);
      }
    );
  }

  // Authentication endpoints
  async validateToken() {
    const response = await this.api.post('/api/auth/validate');
    return response.data;
  }

  // User management
  async getUserProfile() {
    const response = await this.api.get('/api/user/profile');
    return response.data;
  }

  async updateUserProfile(profileData) {
    const response = await this.api.put('/api/user/profile', profileData);
    return response.data;
  }

  // Content management
  async uploadContent(formData) {
    const response = await this.api.post('/api/content/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getContentList() {
    const response = await this.api.get('/api/content/list');
    return response.data;
  }

  async deleteContent(contentId) {
    const response = await this.api.delete(`/api/content/${contentId}`);
    return response.data;
  }


  async deleteWidget(widgetId) {
    try {
      const response = await this.api.delete(`/api/widgets/${widgetId}`);
      return response.data;
    } catch (error) {
      // Handle HTTP errors (404, 500, etc.)
      if (error.response) {
        // Server responded with error status
        throw new Error(
          error.response.data.message ||
          error.response.data.suggestion ||
          'Failed to delete widget'
        );
      } else if (error.request) {
        // Network error
        throw new Error('Network error while deleting widget');
      } else {
        // Other error
        throw new Error('An unexpected error occurred while deleting widget');
      }
    }
  }



  // FAQ management
  async getFAQs() {
    const response = await this.api.get('/api/faqs/list');
    return response.data;
  }

  async optimizeFAQ(faqId, optimizationType) {
    const response = await this.api.post('/api/faqs/optimize', {
      faq_id: faqId,
      optimization_type: optimizationType,
    });
    return response.data;
  }

  async updateFAQ(faqId, question, answer) {
    const response = await this.api.put('/api/faqs/update', {
      faq_id: faqId,
      question,
      answer,
    });
    return response.data;
  }



  async deleteFAQ(faqId) {
    try {
      const response = await this.api.delete(`/api/faqs/${faqId}`);
      return response.data;
    } catch (error) {
      // Handle HTTP errors (404, 500, etc.)
      if (error.response) {
        // Server responded with error status
        throw new Error(
          error.response.data.message ||
          error.response.data.suggestion ||
          'Failed to delete FAQ'
        );
      } else if (error.request) {
        // Network error
        throw new Error('Network error while deleting FAQ');
      } else {
        // Other error
        throw new Error('An unexpected error occurred while deleting FAQ');
      }
    }
  }

  // Widget management
  async createWidget(widgetType, configuration) {
    const response = await this.api.post('/api/widgets/create', {
      widget_type: widgetType,
      configuration,
    });
    return response.data;
  }
  async getWidgets() {
    const response = await this.api.get('/api/widgets/list');
    return response.data;
  }
  async updateWidgetSettings(widgetId, settings) {
    const response = await this.api.put(`/api/widgets/${widgetId}/settings`, settings);
    return response.data;
  }

  async getWidgetConfig(widgetId) {
    const response = await this.api.get(`/api/widgets/${widgetId}/config`);
    return response.data;
  }

  // Chat functionality
  async chatQuery(query) {
    const response = await this.api.post('/api/chat/query', {
      query,
    });
    return response.data;
  }

  // Support queries
  async submitSupportQuery(customerEmail, query, name, mobile) {
    const response = await this.api.post('/api/support/submit', {
      customer_email: customerEmail,
      name,
      mobile,
      query,
    });
    return response.data;
  }

  async submitManualResponse(queryId, customerEmail, humanResponse) {
    const response = await this.api.post('/api/support/submit/manual', {
      query_id: queryId,
      customer_email: customerEmail,
      human_response: humanResponse,
    });
    return response.data;
  }

  async downloadCSVforSupportQueries(requiredStatus, startDate, endDate) {
    const response = await this.api.post('/api/support/export/queries',
      {
        required_status: requiredStatus,
        start_date: startDate || null,
        end_date: endDate || null
      },  //array of required status 
      { responseType: 'blob' }
    );
    return response;
  }

  async getSupportQueries() {
    const response = await this.api.get('/api/support/queries');
    return response.data;
  }

  // Full website scrape
  async fetchSitemapURLs() {
    const response = await this.api.get('/api/website/discover');
    return response.data;
  }

  async createCloudTasks(urlList) {
    const response = await this.api.post('/api/website/create_tasks', {
      "url_list": urlList
    });

    return response.data;
  }

  async trackJobProgress(jobID) {
    const response = await this.api.get(`/api/website/jobs/${jobID}/status`);
    return response.data;
  }

  // Billing
  async getBillingUsage() {
    const response = await this.api.get('/api/billing/usage');
    return response.data;
  }

  async addNoteToSupportQuery(supportQueryId, notes) {
    try {
      const response = await this.api.patch('/api/support/update/notes', {
        support_queryId: supportQueryId,
        notes: notes
      });
      return response.data;
    } catch (error) {
      // Handle HTTP errors (404, 500, etc.)
      if (error.response) {
        // Server responded with error status
        throw new Error(error.response.data.detail?.message || error.response.data.message || 'Failed to add note');
      } else if (error.request) {
        // Network error
        throw new Error('Network error while adding note');
      } else {
        // Other error
        throw new Error('An unexpected error occurred while adding note');
      }
    }
  }

  async closeSupportQuery(supportQueryIds, newStatus) {
    try {
      const response = await this.api.patch('/api/support/update/status', {
        support_queryIds: supportQueryIds,
        new_status: newStatus
      });
      return response.data;
    } catch (error) {
      // Handle HTTP errors (404, 500, etc.)
      if (error.response) {
        // Server responded with error status
        throw new Error(
          error.response.data.message ||
          error.response.data.suggestion ||
          'Failed to close support query'
        );
      } else if (error.request) {
        // Network error
        throw new Error('Network error while closing support query');
      } else {
        // Other error
        throw new Error('An unexpected error occurred while closing support query');
      }
    }
  }


  // Organization methods
  async createOrganization(formData) {
    const response = await this.api.post('/api/org/create', formData);
    return response.data;
  }

  async getOrganizationData() {
    const response = await this.api.get('/api/org/');
    return response.data;
  }

  async updateOrgFields(data) {

    try {
      const response = await this.api.put('api/org/', data);
      return response.data;
    }

    catch (error) {
      throw error;
    }
  }


  // Analytics
  async trackAnalytics(data) {
    const response = await this.api.post('/api/analytics/track', data);
    return response.data;
  }

  async getAnalyticsOverview() {
    const response = await this.api.get('/api/analytics/overview'); return response.data;
  }

  async getCSATTrend(params) {
    const response = await this.api.get('/api/analytics/csat/trend', { params });
    return response.data;
  }



  // Dashboard overview
  async getDashboardOverview() {
    const response = await this.api.get('/api/dashboard/overview');
    return response.data;
  }
  // Health check
  async healthCheck() {
    const response = await this.api.get('/api/health');
    return response.data;
  }


}

export default new ApiService();
